import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FloppyDiskIcon as FloppyDisk, WarningCircleIcon as WarningCircle } from "@phosphor-icons/react";

const nfEuro = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const eu = (v) => (v == null ? "—" : nfEuro.format(v));
const BUDGET_L = { tru: "Trasferte / Reperibilità / Urgenze", discrezionale: "Discrezionale", decurtazioni: "Decurtazioni" };

export function SistemistiIndennitaSettings({ anno }) {
  const [cfg, setCfg] = useState(null);
  const [budget, setBudget] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadCfg = () => api.get(`/reportistica/sistemisti/indennita/config?anno=${anno}`).then(({ data }) => setCfg(data)).catch(() => {});
  const loadBudget = () => api.get(`/reportistica/sistemisti/indennita/budget?anno=${anno}`).then(({ data }) => setBudget(data.budget)).catch(() => {});
  useEffect(() => { loadCfg(); loadBudget(); /* eslint-disable-next-line */ }, [anno]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/reportistica/sistemisti/indennita/config", {
        anno,
        budget_tru: Number(cfg.budget_tru) || 0, budget_discrezionale: Number(cfg.budget_discrezionale) || 0, budget_decurtazioni: Number(cfg.budget_decurtazioni) || 0,
        tariffa_trasferta_notte: Number(cfg.tariffa_trasferta_notte) || 0,
        tariffa_reperibilita_responsabile: Number(cfg.tariffa_reperibilita_responsabile) || 0,
        tariffa_reperibilita_secondario: Number(cfg.tariffa_reperibilita_secondario) || 0,
        decurtazione_standard: Number(cfg.decurtazione_standard) || 0,
      });
      setCfg(data); toast.success("Configurazione indennità salvata"); loadBudget();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  const field = (k, label, suffix) => (
    <div key={k}><Label className="text-xs uppercase tracking-wider">{label}</Label>
      <div className="relative mt-1"><Input type="number" step="0.01" value={cfg[k]} onChange={(e) => set(k, e.target.value)} className="rounded-sm" data-testid={`ind-cfg-${k}`} />{suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>}</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="ind-settings">
      {/* Box utilizzo budget */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ind-settings-boxes">
        {budget.map((b) => { const over = b.superamento > 0; return (
          <Card key={b.categoria} className="rounded-sm border-border shadow-none p-4" data-testid={`ind-set-box-${b.categoria}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{BUDGET_L[b.categoria]}</p>
            <div className="flex items-end justify-between mt-1"><div><span className="text-lg font-semibold">{eu(b.utilizzato)}</span><span className="text-xs text-muted-foreground"> / {eu(b.budget)}</span></div><span className={`text-sm font-medium ${over ? "text-rose-600" : "text-muted-foreground"}`}>{b.percentuale}%</span></div>
            <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden"><div className={`h-full ${over ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${Math.min(100, b.percentuale)}%` }} /></div>
            {over ? <p className="text-xs mt-1 text-rose-600 font-medium flex items-center gap-1"><WarningCircle size={12} weight="fill" /> Superamento: {eu(b.superamento)}</p> : <p className="text-xs mt-1 text-muted-foreground">Residuo: {eu(b.residuo)}</p>}
          </Card>
        ); })}
      </div>

      <Card className="rounded-sm border-border shadow-none p-5" data-testid="ind-cfg-form">
        <h4 className="font-heading font-semibold mb-1">Budget annuali {anno}</h4>
        <p className="text-xs text-muted-foreground mb-4">Le modifiche alle tariffe non ricalcolano le registrazioni già inserite (snapshot della tariffa al momento della creazione).</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {field("budget_tru", "Budget Trasferte / Reperibilità / Urgenze", "€")}
          {field("budget_discrezionale", "Budget Discrezionale", "€")}
          {field("budget_decurtazioni", "Budget Decurtazioni", "€")}
        </div>
        <h4 className="font-heading font-semibold mb-3">Tariffe standard</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {field("tariffa_trasferta_notte", "Trasferta con pernottamento", "€/notte")}
          {field("tariffa_reperibilita_responsabile", "Reperibilità Responsabile", "€/giorno")}
          {field("tariffa_reperibilita_secondario", "Reperibilità Secondario", "€/giorno")}
          {field("decurtazione_standard", "Decurtazione standard", "€/evento")}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Urgenze, attività festive e premi discrezionali non hanno tariffa automatica: l'importo è sempre inserito manualmente (evento una tantum).</p>
        <div className="flex justify-end mt-4"><Button className="rounded-sm gap-1.5" onClick={save} disabled={saving} data-testid="ind-cfg-save"><FloppyDisk size={15} /> Salva</Button></div>
      </Card>
    </div>
  );
}
